import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useTheme } from "@mui/material/styles";
import { styled } from '@mui/system';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
  PlaylistAdd as BulkAddIcon,
  RemoveCircleOutline,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Select,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const ENDPOINTS = {
  list: "/hr/leave-types/list/",

  create: "/hr/leave-types/",

  bulkCreate: "/hr/leave-types/bulk-create/",

  detail: (id) => `/hr/leave-types/${id}/`,

  update: (id) => `/hr/leave-types/${id}/update/`,

  delete: (id) => `/hr/leave-types/${id}/delete/`,

  status: (id) => `/hr/leave-types/${id}/status/`,
};

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

/* =========================================================
   AXIOS
========================================================= */

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const tokenKeys = [
    "accessToken",
    "access_token",
    "token",
  ];

  for (const key of tokenKeys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   HELPERS
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(
      sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
    );
  } catch (error) {
    console.error("Unable to read logged-in user:", error);

    return {};
  }
};

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const getEmptyForm = () => ({
  leave_name: "",
  leave_code: "",
  description: "",
  annual_days: "",
  is_paid: true,
  requires_approval: true,
});

const getEmptyBulkRow = () => ({
  leave_name: "",
  leave_code: "",
  description: "",
  annual_days: "",
  is_paid: true,
  requires_approval: true,
});

/* =========================================================
   COMPONENT
========================================================= */

const LeaveType = () => {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  // Global branch selector (navbar)
  const { selectedBranchId } = useBranch();

  const currentUser = useMemo(
    () => getStoredUser(),
    []
  );

  const organizationId =
    currentUser?.organization_id ||
    currentUser?.organization?.id ||
    "";

  const textPrimary = theme.palette.text.primary;

  const textSecondary = theme.palette.text.disabled;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     DATA

     Swagger /list/ returns a plain array —
     server-side: search + is_paid,
     client-side: status filter + pagination.
  ======================================================= */

  const [rows, setRows] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);

  const [bulkSaving, setBulkSaving] = useState(false);

  /* =======================================================
     SEARCH / FILTERS

     Swagger list params:
     organization, branch, is_paid, search
  ======================================================= */

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  const [isPaidFilter, setIsPaidFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("All");

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     ADD / EDIT
  ======================================================= */

  const [dialogOpen, setDialogOpen] = useState(false);

  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState(getEmptyForm());

  /* =======================================================
     BULK CREATE
  ======================================================= */

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const [bulkRows, setBulkRows] = useState([getEmptyBulkRow()]);

  /* =======================================================
     DELETE
  ======================================================= */

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);

  /* =======================================================
     STATUS
  ======================================================= */

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const [statusItem, setStatusItem] = useState(null);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     DIALOG STYLE
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,

    color: textPrimary,

    border: `1px solid ${theme.palette.divider}`,

    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

  /* =======================================================
     SELECT STYLE
  ======================================================= */

  const selectStyles = {
    borderRadius: 2,

    backgroundColor: inputBackground,

    color: textPrimary,

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "& .MuiSvgIcon-root": {
      color: textPrimary,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,

        color: textPrimary,

        border: `1px solid ${theme.palette.divider}`,

        "& .MuiMenuItem-root": {
          color: textPrimary,

          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },

          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",

            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     TEXT FIELD STYLE
  ======================================================= */

 const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    color: textPrimary,

    backgroundColor: theme.palette.background.paper,

    "& fieldset": {
      borderColor,
    },

    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },

  "& .MuiInputBase-input": {
    color: textPrimary,

    WebkitTextFillColor: textPrimary,
  },

  "& .MuiInputBase-input::placeholder": {
    color: `${textPrimary} !important`,

    WebkitTextFillColor: `${textPrimary} !important`,

    opacity: "0.65 !important",
  },

  "& .MuiInputLabel-root": {
    color: textPrimary,

    opacity: 0.75,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.primary.main,

    opacity: 1,
  },
};

  /* =======================================================
     ERROR MESSAGE
  ======================================================= */

  const getErrorMessage = (error, fallback) => {
    console.error("API ERROR:", error);

    if (error?.response?.status === 401) {
      return "Unauthorized. Please login again.";
    }

    if (error?.response?.status === 403) {
      return "You do not have permission to access this API.";
    }

    const data = error?.response?.data;

    if (!data) {
      return error?.message || fallback || "Something went wrong.";
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.detail) {
      return String(data.detail);
    }

    if (data.message) {
      return typeof data.message === "string"
        ? data.message
        : JSON.stringify(data.message);
    }

    if (data.error) {
      return typeof data.error === "string"
        ? data.error
        : JSON.stringify(data.error);
    }

    if (typeof data === "object") {
      return Object.values(data)
        .flat()
        .join(" ");
    }

    return fallback || "Something went wrong.";
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /* =======================================================
     FETCH LEAVE TYPES

     GET /hr/leave-types/list/
     Server-side params (Swagger):
     organization, branch, is_paid, search
  ======================================================= */

  const fetchLeaveTypes = useCallback(
    async ({
      branch = selectedBranchId,
      searchValue = search,
      paidFilter = isPaidFilter,
    } = {}) => {
      if (!organizationId || !branch) {
        setRows([]);
        return;
      }

      try {
        setLoading(true);

        const params = {
          organization: organizationId,
          branch: branch,
        };

        if (searchValue && searchValue.trim()) {
          params.search = searchValue.trim();
        }

        if (
          paidFilter !== "" &&
          paidFilter !== null &&
          paidFilter !== undefined
        ) {
          params.is_paid = paidFilter;
        }

        const response = await api.get(ENDPOINTS.list, {
          params,
        });

        setRows(normalizeArray(response.data));
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Failed to load leave types."),
          "error"
        );

        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [
      organizationId,
      selectedBranchId,
      search,
      isPaidFilter,
      showSnackbar,
    ]
  );

  /* =======================================================
     BRANCH CHANGE / INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    setPage(0);

    fetchLeaveTypes({
      branch: selectedBranchId,
    });
  }, [selectedBranchId]);

  /* =======================================================
     SEARCH (server-side)
  ======================================================= */

  const handleSearch = () => {
    const value = searchInput.trim();

    setSearch(value);

    setPage(0);

    fetchLeaveTypes({
      searchValue: value,
      currentPage: 0,
    });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);

    fetchLeaveTypes({
      searchValue: "",
      currentPage: 0,
    });
  };

  /* =======================================================
     CLIENT-SIDE STATUS FILTER
  ======================================================= */

  const filteredLeaveTypes = useMemo(() => {
    if (statusFilter === "All") {
      return rows;
    }

    const wantActive = statusFilter === "Active";

    return rows.filter(
      (item) => Boolean(item?.is_active) === wantActive
    );
  }, [rows, statusFilter]);

  const displayedLeaveTypes = filteredLeaveTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     ADD
  ======================================================= */

  const handleAdd = () => {
    setEditingItem(null);

    setFormData(getEmptyForm());

    setDialogOpen(true);
  };

  /* =======================================================
     BULK CREATE OPEN
  ======================================================= */

  const handleOpenBulkDialog = () => {
    setBulkRows([getEmptyBulkRow()]);

    setBulkDialogOpen(true);
  };

  /* =======================================================
     BULK ROW HELPERS
  ======================================================= */

  const updateBulkRow = (index, name, value) => {
    setBulkRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [name]: value } : row
      )
    );
  };

  const addBulkRow = () => {
    setBulkRows((prev) => [...prev, getEmptyBulkRow()]);
  };

  const removeBulkRow = (index) => {
    setBulkRows((prev) =>
      prev.length === 1
        ? prev
        : prev.filter((_, i) => i !== index)
    );
  };

  /* =======================================================
     BULK SUBMIT

     POST /hr/leave-types/bulk-create/
     Payload (Swagger):
     {
       leave_types: [{
         organization, branch, leave_name,
         leave_code, description, annual_days,
         is_paid, requires_approval
       }]
     }
  ======================================================= */

  const handleBulkSubmit = async () => {
    if (!organizationId || !selectedBranchId) {
      showSnackbar(
        "Organization / branch not found. Please select a branch.",
        "error"
      );

      return;
    }

    for (let i = 0; i < bulkRows.length; i++) {
      const row = bulkRows[i];

      if (!row.leave_name.trim()) {
        showSnackbar(
          `Row ${i + 1}: Leave name is required.`,
          "error"
        );

        return;
      }

      if (!row.leave_code.trim()) {
        showSnackbar(
          `Row ${i + 1}: Leave code is required.`,
          "error"
        );

        return;
      }

      if (row.annual_days === "" || Number(row.annual_days) < 0) {
        showSnackbar(
          `Row ${i + 1}: Please enter valid annual days.`,
          "error"
        );

        return;
      }
    }

    try {
      setBulkSaving(true);

      const payload = {
        leave_types: bulkRows.map((row) => ({
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
          leave_name: row.leave_name.trim(),
          leave_code: row.leave_code.trim(),
          description: row.description.trim() || null,
          annual_days: Number(row.annual_days),
          is_paid: Boolean(row.is_paid),
          requires_approval: Boolean(row.requires_approval),
        })),
      };

      const response = await api.post(
        ENDPOINTS.bulkCreate,
        payload
      );

      showSnackbar(
        response?.data?.message ||
          "Leave types created successfully.",
        "success"
      );

      setBulkDialogOpen(false);

      await fetchLeaveTypes();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Failed to create leave types."),
        "error"
      );
    } finally {
      setBulkSaving(false);
    }
  };

  /* =======================================================
     EDIT (uses row data; detail endpoint also available)
  ======================================================= */

  const handleEdit = async (item) => {
    setEditingItem(item);

    setFormData({
      leave_name: item?.leave_name || "",
      leave_code: item?.leave_code || "",
      description: item?.description || "",
      annual_days: item?.annual_days ?? "",
      is_paid:
        item?.is_paid === undefined
          ? true
          : Boolean(item.is_paid),
      requires_approval:
        item?.requires_approval === undefined
          ? true
          : Boolean(item.requires_approval),
    });

    setDialogOpen(true);

    /* Refresh from detail endpoint to make sure
       the form has the latest values. */

    try {
      const response = await api.get(
        ENDPOINTS.detail(item.id)
      );

      const d = response.data?.data ?? response.data;

      if (d && d.id) {
        setFormData({
          leave_name: d.leave_name || "",
          leave_code: d.leave_code || "",
          description: d.description || "",
          annual_days: d.annual_days ?? "",
          is_paid: Boolean(d.is_paid),
          requires_approval: Boolean(d.requires_approval),
        });
      }
    } catch (error) {
      console.error("Detail fetch error:", error);
    }
  };

  /* =======================================================
     CLOSE FORM
  ======================================================= */

  const handleCloseDialog = () => {
    if (saving) return;

    setDialogOpen(false);

    setEditingItem(null);
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     SAVE

     POST /hr/leave-types/            (create)
     PUT  /hr/leave-types/{id}/update/ (update)

     Payload (Swagger):
     {
       organization, branch, leave_name, leave_code,
       description, annual_days, is_paid, requires_approval
     }
  ======================================================= */

  const handleSave = async () => {
    if (!formData.leave_name.trim()) {
      showSnackbar("Please enter leave type name.", "error");

      return;
    }

    if (!formData.leave_code.trim()) {
      showSnackbar("Please enter leave code.", "error");

      return;
    }

    if (
      formData.annual_days === "" ||
      Number(formData.annual_days) < 0
    ) {
      showSnackbar("Please enter valid annual days.", "error");

      return;
    }

    if (!organizationId || !selectedBranchId) {
      showSnackbar(
        "Organization / branch not found. Please select a branch.",
        "error"
      );

      return;
    }

    try {
      setSaving(true);

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        leave_name: formData.leave_name.trim(),
        leave_code: formData.leave_code.trim(),
        description: formData.description.trim() || null,
        annual_days: String(formData.annual_days),
        is_paid: Boolean(formData.is_paid),
        requires_approval: Boolean(formData.requires_approval),
      };

      if (!editingItem) {
        const response = await api.post(
          ENDPOINTS.create,
          payload
        );

        showSnackbar(
          response?.data?.message ||
            "Leave type created successfully.",
          "success"
        );
      } else {
        const response = await api.put(
          ENDPOINTS.update(editingItem.id),
          payload
        );

        showSnackbar(
          response?.data?.message ||
            "Leave type updated successfully.",
          "success"
        );
      }

      setDialogOpen(false);

      setEditingItem(null);

      await fetchLeaveTypes();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Failed to save leave type."),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     DELETE OPEN / CLOSE
  ======================================================= */

  const handleDeleteClick = (item) => {
    setDeleteItem(item);

    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    if (deleting) return;

    setDeleteDialogOpen(false);

    setDeleteItem(null);
  };

  /* =======================================================
     DELETE

     DELETE /hr/leave-types/{id}/delete/  (soft delete)
  ======================================================= */

  const handleDelete = async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);

      const response = await api.delete(
        ENDPOINTS.delete(deleteItem.id)
      );

      showSnackbar(
        response?.data?.message ||
          "Leave type deleted successfully.",
        "success"
      );

      setDeleteDialogOpen(false);

      setDeleteItem(null);

      await fetchLeaveTypes();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Failed to delete leave type."),
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  /* =======================================================
     STATUS OPEN / CLOSE
  ======================================================= */

  const handleStatusClick = (item) => {
    setStatusItem(item);

    setStatusDialogOpen(true);
  };

  const handleCloseStatus = () => {
    if (statusUpdating) return;

    setStatusDialogOpen(false);

    setStatusItem(null);
  };

  /* =======================================================
     STATUS UPDATE

     PATCH /hr/leave-types/{id}/status/
     Payload: { is_active }
  ======================================================= */

  const handleStatusUpdate = async () => {
    if (!statusItem?.id) return;

    const newStatus = !Boolean(statusItem?.is_active);

    try {
      setStatusUpdating(true);

      const response = await api.patch(
        ENDPOINTS.status(statusItem.id),
        { is_active: newStatus }
      );

      /* LOCAL UPDATE — no full reload needed */

      setRows((prev) =>
        prev.map((item) =>
          item.id === statusItem.id
            ? { ...item, is_active: newStatus }
            : item
        )
      );

      showSnackbar(
        response?.data?.message ||
          `Leave type ${
            newStatus ? "activated" : "deactivated"
          } successfully.`,
        "success"
      );

      setStatusDialogOpen(false);

      setStatusItem(null);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Failed to update status."),
        "error"
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  /* =======================================================
     REFRESH
  ======================================================= */

  const handleRefresh = async () => {
    await fetchLeaveTypes();
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =======================================================
     APPLY / RESET FILTER
  ======================================================= */

  const [filterStatusDraft, setFilterStatusDraft] =
    useState("All");

  const [filterPaidDraft, setFilterPaidDraft] = useState("");

  const handleOpenFilters = () => {
    setFilterStatusDraft(statusFilter);

    setFilterPaidDraft(isPaidFilter);

    setFilterModalOpen(true);
  };

  const handleApplyFilter = async () => {
    setStatusFilter(filterStatusDraft);

    setIsPaidFilter(filterPaidDraft);

    setPage(0);

    setFilterModalOpen(false);

    await fetchLeaveTypes({
      paidFilter: filterPaidDraft,
      currentPage: 0,
    });
  };

  const handleResetFilter = async () => {
    setStatusFilter("All");

    setIsPaidFilter("");

    setFilterStatusDraft("All");

    setFilterPaidDraft("");

    setSearchInput("");

    setSearch("");

    setPage(0);

    setFilterModalOpen(false);

    await fetchLeaveTypes({
      paidFilter: "",
      searchValue: "",
      currentPage: 0,
    });
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },

        minHeight: "100vh",

        color: textPrimary,
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",

          justifyContent: "space-between",

          alignItems: { xs: "stretch", md: "center" },

          flexDirection: { xs: "column", md: "row" },

          gap: 2,

          mb: 3,
        }}
      >
        {/* SEARCH (server-side) */}

        <TextField
          size="small"
          variant="outlined"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search leave type, code..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  size="small"
                  onClick={handleSearch}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),

            endAdornment:
              searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
          }}
          sx={{
            width: { xs: "100%", sm: 340, md: 360 },

            "& .MuiOutlinedInput-root": {
              height: 50,

              borderRadius: "30px",

              backgroundColor: inputBackground,

              color: textPrimary,

              "& input": {
                color: textPrimary,

                WebkitTextFillColor: textPrimary,
              },

              "& input::placeholder": {
                color: textPrimary,

                opacity: 0.85,
              },

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,

                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",

            gap: 1,

            alignItems: "center",

            flexWrap: "wrap",

            justifyContent: {
              xs: "flex-start",
              md: "flex-end",
            },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 40,

              textTransform: "none",

              fontWeight: 600,

              borderRadius: 1.5,
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleOpenFilters}
            sx={{
              height: 40,

              textTransform: "none",

              fontWeight: 600,

              borderRadius: 1.5,
            }}
          >
            Filters
          </Button>

          {/* BULK CREATE */}

          <Button
            variant="contained"
            startIcon={<BulkAddIcon />}
            onClick={handleOpenBulkDialog}
            sx={{
              height: 40,

              textTransform: "none",

              fontWeight: 600,

              borderRadius: 1.5,
            }}
          >
            Bulk Create
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              height: 40,

              textTransform: "none",

              fontWeight: 600,

              borderRadius: 1.5,
            }}
          >
            Add Leave Type
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          FILTER MODAL
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,

            overflow: "hidden",

            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,

            py: 2.5,

            color: textPrimary,
          }}
        >
          <Box
            sx={{
              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: textPrimary }}
            >
              Filter Leave Types
            </Typography>

            <IconButton
              size="small"
              onClick={() => setFilterModalOpen(false)}
              sx={{
                color: textPrimary,

                border: `1px solid ${borderColor}`,

                "&:hover": {
                  backgroundColor:
                    theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,

            py: 3,

            backgroundColor:
              theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",

              flexDirection: "column",

              gap: 2.5,
            }}
          >
            {/* STATUS (client-side) */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",

                  mb: 1,

                  letterSpacing: 0.5,

                  color: theme.palette.primary.main,
                }}
              >
                STATUS
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterStatusDraft}
                  onChange={(event) =>
                    setFilterStatusDraft(event.target.value)
                  }
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="All">
                    All Statuses
                  </MenuItem>

                  <MenuItem value="Active">
                    Active
                  </MenuItem>

                  <MenuItem value="Inactive">
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* PAID (server-side is_paid) */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",

                  mb: 1,

                  letterSpacing: 0.5,

                  color: theme.palette.primary.main,
                }}
              >
                PAID / UNPAID
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterPaidDraft}
                  onChange={(event) =>
                    setFilterPaidDraft(event.target.value)
                  }
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="true">Paid</MenuItem>

                  <MenuItem value="false">Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* ORGANIZATION (read-only info) */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",

                  mb: 1,

                  letterSpacing: 0.5,

                  color: theme.palette.primary.main,
                }}
              >
                ORGANIZATION
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${borderColor}`,

                  borderRadius: 2,

                  px: 2,

                  py: 1.4,

                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: textPrimary }}
                >
                  {currentUser?.organization_name ||
                    organizationId ||
                    "-"}
                </Typography>
              </Box>
            </Box>

            {/* BRANCH (from global selector) */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",

                  mb: 1,

                  letterSpacing: 0.5,

                  color: theme.palette.primary.main,
                }}
              >
                BRANCH
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${borderColor}`,

                  borderRadius: 2,

                  px: 2,

                  py: 1.4,

                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: textPrimary }}
                >
                  (From global branch selector)
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,

            py: 2,

            gap: 1,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="text"
            onClick={handleResetFilter}
            sx={{
              textTransform: "none",

              color: textPrimary,
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilter}
            sx={{
              textTransform: "none",

              fontWeight: 600,
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          MAIN TABLE CARD
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,

          borderRadius: 2,

          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,

            "&:last-child": {
              pb: 0,
            },
          }}
        >
          {/* TABLE */}

         <TableContainer
  component={Paper}
  elevation={0}
  sx={{
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
  }}
>
            <CustomTableScrollbar style={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1150 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.primary.main,

                    "& .MuiTableCell-root": {
                      color: "#FFFFFF",

                      fontWeight: 700,

                      fontSize: "0.82rem",

                      borderBottom: "none",

                      py: 1.8,

                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Leave Type</TableCell>

                  <TableCell>Code</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Description</TableCell>

                  <TableCell>Annual Days</TableCell>

                  <TableCell>Paid</TableCell>

                  <TableCell>Approval</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{
                        py: 7,

                        borderBottom: "none",
                      }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{
                          color: theme.palette.primary.main,
                        }}
                      />

                      <Typography
                        sx={{
                          mt: 1.5,

                          color: textPrimary,
                        }}
                      >
                        Loading leave types...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedLeaveTypes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{
                        py: 8,

                        borderBottom: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,

                          fontSize: "0.95rem",

                          color: textPrimary,
                        }}
                      >
                        No leave types found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.7,

                          color: textPrimary,
                        }}
                      >
                        Try changing your search or filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedLeaveTypes.map(
                    (item, index) => {
                      const isActive = Boolean(item?.is_active);

                      return (
                        <TableRow
                          key={item?.id ?? index}
                          hover
                          sx={{
                            transition:
                              "background-color 0.2s ease",

                            "&:hover": {
                              backgroundColor:
                                theme.palette.action.hover,
                            },

                            "& .MuiTableCell-root": {
                              borderBottom: `1px solid ${borderColor}`,

                              py: 1.5,

                              fontSize: "0.82rem",

                              color: textPrimary,
                            },
                          }}
                        >
                          {/* S.NO */}

                          <TableCell sx={{ fontWeight: 600 }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          {/* LEAVE TYPE */}

                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,

                                fontSize: "0.84rem",

                                color: textPrimary,

                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.leave_name || "-"}
                            </Typography>
                          </TableCell>

                          {/* CODE */}

                          <TableCell>
                            <Chip
                              size="small"
                              label={item?.leave_code || "-"}
                              sx={{
                                height: 25,

                                fontSize: "0.7rem",

                                fontWeight: 700,

                                borderRadius: "6px",

                                backgroundColor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.05)",

                                color: textPrimary,
                              }}
                            />
                          </TableCell>

                          {/* BRANCH (from API row) */}

                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 600,

                                whiteSpace: "nowrap",

                                color: textPrimary,
                              }}
                            >
                              {item?.branch_name || "-"}
                            </Typography>
                          </TableCell>

                          {/* DESCRIPTION */}

                          <TableCell>
                            <Tooltip
                              title={item?.description || ""}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 230,

                                  overflow: "hidden",

                                  textOverflow: "ellipsis",

                                  whiteSpace: "nowrap",

                                  color: textPrimary,
                                }}
                              >
                                {item?.description || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ANNUAL DAYS */}

                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,

                                color: textPrimary,
                              }}
                            >
                              {item?.annual_days ?? "—"}
                            </Typography>
                          </TableCell>

                          {/* PAID */}

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                item?.is_paid ? "Paid" : "Unpaid"
                              }
                              sx={{
                                height: 26,

                                fontSize: "0.7rem",

                                fontWeight: 700,

                                borderRadius: "6px",

                                backgroundColor: item?.is_paid
                                  ? isDark
                                    ? "rgba(34,197,94,0.18)"
                                    : "#DCFCE7"
                                  : isDark
                                  ? "rgba(148,163,184,0.18)"
                                  : "#F1F5F9",

                                color: item?.is_paid
                                  ? isDark
                                    ? "#4ADE80"
                                    : "#15803D"
                                  : isDark
                                  ? "#CBD5E1"
                                  : "#64748B",
                              }}
                            />
                          </TableCell>

                          {/* APPROVAL */}

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                item?.requires_approval
                                  ? "Required"
                                  : "Not Required"
                              }
                              variant="contained"
                              sx={{
                                height: 26,

                                fontSize: "0.7rem",

                                fontWeight: 600,

                                borderRadius: "6px",

                                color: textPrimary,

                                borderColor,
                              }}
                            />
                          </TableCell>

                          {/* STATUS */}

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                isActive ? "Active" : "Inactive"
                              }
                              sx={{
                                height: 26,

                                fontSize: "0.72rem",

                                fontWeight: 700,

                                borderRadius: "6px",

                                backgroundColor: isActive
                                  ? isDark
                                    ? "rgba(34,197,94,0.18)"
                                    : "#DCFCE7"
                                  : isDark
                                  ? "rgba(148,163,184,0.18)"
                                  : "#F1F5F9",

                                color: isActive
                                  ? isDark
                                    ? "#4ADE80"
                                    : "#15803D"
                                  : isDark
                                  ? "#CBD5E1"
                                  : "#64748B",
                              }}
                            />
                          </TableCell>

                          {/* ACTIONS */}

                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",

                                justifyContent: "center",

                                alignItems: "center",

                                gap: 0.7,
                              }}
                            >
                              {/* STATUS */}

                              <Tooltip
                                title={
                                  isActive
                                    ? "Deactivate"
                                    : "Activate"
                                }
                              >
                                <Switch
                                  size="small"
                                  checked={isActive}
                                  onChange={() =>
                                    handleStatusClick(item)
                                  }
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked":
                                      {
                                        color:
                                          theme.palette.primary.main,
                                      },

                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor:
                                          theme.palette.primary.main,
                                      },
                                  }}
                                />
                              </Tooltip>

                              {/* EDIT */}

                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(item)}
                                  sx={{
                                    width: 32,

                                    height: 32,

                                    color: theme.palette.primary.main,

                                    backgroundColor: isDark
                                      ? `${theme.palette.primary.main}22`
                                      : `${theme.palette.primary.main}12`,

                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.primary.main,

                                      color: "#FFFFFF",
                                    },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* DELETE */}

                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeleteClick(item)
                                  }
                                  sx={{
                                    width: 32,

                                    height: 32,

                                    color:
                                      theme.palette.error.main,

                                    backgroundColor: isDark
                                      ? `${theme.palette.error.main}22`
                                      : `${theme.palette.error.main}12`,

                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.error.main,

                                      color: "#FFFFFF",
                                    },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )
                )}
              </TableBody>
            </Table>
            </CustomTableScrollbar>
          </TableContainer>

          {/* PAGINATION */}

          <TablePagination
            component="div"
            count={filteredLeaveTypes.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,

              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },

              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          ADD / EDIT MODAL
      =================================================== */}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,

            overflow: "hidden",

            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,

            py: 2.5,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: textPrimary }}
            >
              {editingItem
                ? "Edit Leave Type"
                : "Add Leave Type"}
            </Typography>

            <IconButton
              size="small"
              onClick={handleCloseDialog}
              disabled={saving}
              sx={{
                color: textPrimary,

                border: `1px solid ${borderColor}`,

                "&:hover": {
                  backgroundColor:
                    theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

             <CustomTableScrollbar
    style={{
      maxHeight: 480,
      overflowY: 'auto',
       backgroundColor: theme.palette.background.paper,
    }}
  >

        <DialogContent
          sx={{
            px: 3,

            py: 3,

            backgroundColor: theme.palette.background.paper,
          }}
        >
      
          <Box
            sx={{
              display: "flex",

              flexDirection: "column",

              gap: 2.5,
            }}
          >
            {/* LEAVE NAME */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",

                  mb: 1,

                  fontWeight: 700,

                  letterSpacing: 0.6,

                  color: theme.palette.primary.main,
                }}
              >
                LEAVE NAME
              </Typography>

              <TextField
                fullWidth
                size="small"
                name="leave_name"
                value={formData.leave_name}
                onChange={handleFormChange}
                placeholder="e.g. Annual Leave"
                required
                sx={textFieldStyles}
              />
            </Box>

            {/* LEAVE CODE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",

                  mb: 1,

                  fontWeight: 700,

                  letterSpacing: 0.6,

                  color: theme.palette.primary.main,
                }}
              >
                LEAVE CODE
              </Typography>

              <TextField
                fullWidth
                size="small"
                name="leave_code"
                value={formData.leave_code}
                onChange={handleFormChange}
                placeholder="e.g. AL"
                required
                sx={textFieldStyles}
              />
            </Box>

            {/* DESCRIPTION */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",

                  mb: 1,

                  fontWeight: 700,

                  letterSpacing: 0.6,

                  color: theme.palette.primary.main,
                }}
              >
                DESCRIPTION
              </Typography>

              <TextField
                fullWidth
                size="small"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Enter leave description"
                multiline
                minRows={3}
                sx={textFieldStyles}
              />
            </Box>

            {/* ANNUAL DAYS */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",

                  mb: 1,

                  fontWeight: 700,

                  letterSpacing: 0.6,

                  color: theme.palette.primary.main,
                }}
              >
                ANNUAL DAYS
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="number"
                name="annual_days"
                value={formData.annual_days}
                onChange={handleFormChange}
                placeholder="e.g. 18"
                inputProps={{ min: 0, step: "0.5" }}
                sx={textFieldStyles}
              />
            </Box>

            {/* PAID */}

            <Box
              sx={{
                display: "flex",

                justifyContent: "space-between",

                alignItems: "center",

                border: `1px solid ${borderColor}`,

                borderRadius: 2,

                px: 2,

                py: 1.4,

                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box>
                <Typography
                  fontWeight={700}
                  sx={{ color: textPrimary }}
                >
                  Paid Leave
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: textPrimary }}
                >
                  Is this leave paid?
                </Typography>
              </Box>

              <Switch
                checked={Boolean(formData.is_paid)}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,

                    is_paid: event.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },

                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                    {
                      backgroundColor:
                        theme.palette.primary.main,
                    },
                }}
              />
            </Box>

            {/* APPROVAL */}

            <Box
              sx={{
                display: "flex",

                justifyContent: "space-between",

                alignItems: "center",

                border: `1px solid ${borderColor}`,

                borderRadius: 2,

                px: 2,

                py: 1.4,

                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box>
                <Typography
                  fontWeight={700}
                  sx={{ color: textPrimary }}
                >
                  Requires Approval
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: textPrimary }}
                >
                  Manager approval required?
                </Typography>
              </Box>

              <Switch
                checked={Boolean(formData.requires_approval)}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,

                    requires_approval: event.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },

                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                    {
                      backgroundColor:
                        theme.palette.primary.main,
                    },
                }}
              />
            </Box>
          </Box>

        </DialogContent>

        </CustomTableScrollbar>

        <Divider />

        <DialogActions
          sx={{
            px: 3,

            py: 2,

            gap: 1.2,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,

              color: textPrimary,

              borderColor,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save fontSize="small" />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth: 150,

              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,
            }}
          >
            {saving
              ? "Saving..."
              : editingItem
              ? "Update Leave Type"
              : "Create Leave Type"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          BULK CREATE MODAL

          POST /hr/leave-types/bulk-create/
          Payload: { leave_types: [...] }
      =================================================== */}

    <Dialog
  open={bulkDialogOpen}
  onClose={() => !bulkSaving && setBulkDialogOpen(false)}
  fullWidth
  maxWidth="md"
  PaperProps={{
    sx: {
      borderRadius: 3,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      maxHeight: "90vh",
      ...dialogPaperStyles,
    },
  }}
>
        <DialogTitle
  sx={{
    px: 3,
    py: 2.5,
    backgroundColor: theme.palette.background.paper,
  }}
>
          <Box
            sx={{
              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: textPrimary }}
              >
                Bulk Create Leave Types
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: textSecondary }}
              >
                Add multiple leave types for the selected
                branch in one request.
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() =>
                !bulkSaving && setBulkDialogOpen(false)
              }
              disabled={bulkSaving}
              sx={{
                color: textPrimary,

                border: `1px solid ${borderColor}`,

                "&:hover": {
                  backgroundColor:
                    theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />
<CustomTableScrollbar
    style={{
      maxHeight: 480,
      overflowY: 'auto',
      padding: '14px',
      backgroundColor: theme.palette.background.paper,
    }}
  >
     <DialogContent
  sx={{
    p: 0,
    overflowY: "hidden",
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
  }}
>
          <Box
            sx={{
              display: "flex",

              flexDirection: "column",

              gap: 2.5,
                  pb: 1,
            }}
          >
            {bulkRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,

                  border: `1px solid ${borderColor}`,

                  borderRadius: 2,

                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Box
                  sx={{
                    display: "flex",

                    justifyContent: "space-between",

                    alignItems: "center",

                    mb: 2,
                  }}
                >
                  <Typography
                    fontWeight={700}
                    sx={{ color: textPrimary }}
                  >
                    Leave Type #{index + 1}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => removeBulkRow(index)}
                    disabled={
                      bulkRows.length === 1 || bulkSaving
                    }
                    sx={{
                      color: theme.palette.error.main,
                    }}
                  >
                    <RemoveCircleOutline fontSize="small" />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns: {
                      xs: "1fr",

                      sm: "repeat(2, minmax(0, 1fr))",
                    },

                    gap: 2,
                  }}
                >
                  {/* LEAVE NAME */}

                  <TextField
                    fullWidth
                    size="small"
                    label="Leave Name *"
                    value={row.leave_name}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "leave_name",
                        e.target.value
                      )
                    }
                    placeholder="e.g. Casual Leave"
                    sx={textFieldStyles}
                  />

                  {/* LEAVE CODE */}

                  <TextField
                    fullWidth
                    size="small"
                    label="Leave Code *"
                    value={row.leave_code}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "leave_code",
                        e.target.value
                      )
                    }
                    placeholder="e.g. CL"
                    sx={textFieldStyles}
                  />

                  {/* ANNUAL DAYS */}

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Annual Days *"
                    value={row.annual_days}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "annual_days",
                        e.target.value
                      )
                    }
                    inputProps={{ min: 0, step: "0.5" }}
                    sx={textFieldStyles}
                  />

                  {/* DESCRIPTION */}

                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={row.description}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    sx={textFieldStyles}
                  />
                </Box>

                {/* SWITCHES */}

                <Box
                  sx={{
                    display: "flex",

                    gap: 4,

                    mt: 2,

                    flexWrap: "wrap",
                  }}
                >
                  {/* PAID */}

                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      gap: 1,
                    }}
                  >
                    <Switch
                      size="small"
                      checked={Boolean(row.is_paid)}
                      onChange={(e) =>
                        updateBulkRow(
                          index,
                          "is_paid",
                          e.target.checked
                        )
                      }
                    />

                    <Typography
                      variant="body2"
                      sx={{ color: textPrimary }}
                    >
                      Paid
                    </Typography>
                  </Box>

                  {/* APPROVAL */}

                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      gap: 1,
                    }}
                  >
                    <Switch
                      size="small"
                      checked={Boolean(row.requires_approval)}
                      onChange={(e) =>
                        updateBulkRow(
                          index,
                          "requires_approval",
                          e.target.checked
                        )
                      }
                    />

                    <Typography
                      variant="body2"
                      sx={{ color: textPrimary }}
                    >
                      Requires Approval
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* ADD ROW */}

           
          </Box>
        </DialogContent>
          </CustomTableScrollbar>

        <Divider />

        <DialogActions
          sx={{
            px: 3,

            py: 2,

            gap: 1.2,

            backgroundColor: theme.palette.background.paper,
          }}
        >

          <Button
  variant="contained"
  startIcon={<Add />}
  onClick={addBulkRow}
  disabled={bulkSaving}
  sx={{
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,
    color: textPrimary,
    borderColor,
    mr: "auto",
  }}
>
  Add Another Leave Type
</Button>
            
          <Button
            variant="contained"
            onClick={() => setBulkDialogOpen(false)}
            disabled={bulkSaving}
            sx={{
              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,

              color: textPrimary,

              borderColor,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              bulkSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <BulkAddIcon fontSize="small" />
              )
            }
            onClick={handleBulkSubmit}
            disabled={bulkSaving}
            sx={{
              minWidth: 170,

              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,
            }}
          >
            {bulkSaving
              ? "Creating..."
              : `Create ${bulkRows.length} Leave Type${
                  bulkRows.length > 1 ? "s" : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DELETE MODAL (soft delete)
      =================================================== */}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,

            overflow: "hidden",

            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,

            py: 2.5,

            color: textPrimary,

            fontWeight: 700,
          }}
        >
          Delete Leave Type
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            py: 3,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography
            sx={{
              color: textPrimary,

              fontWeight: 600,
            }}
          >
            Are you sure you want to delete this leave
            type?
          </Typography>

          {deleteItem && (
            <Box
              sx={{
                mt: 2,

                p: 1.8,

                border: `1px solid ${borderColor}`,

                borderRadius: 2,

                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,

                  color: textPrimary,
                }}
              >
                {deleteItem.leave_name || "-"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.4,

                  color: textPrimary,
                }}
              >
                Code: {deleteItem.leave_code || "-"}
              </Typography>
            </Box>
          )}

          <Typography
            variant="body2"
            sx={{
              mt: 1.5,

              color: textSecondary,
            }}
          >
            This is a soft delete — the leave type can be
            restored later by activating it.
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,

            py: 2,

            gap: 1,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDelete}
            disabled={deleting}
            sx={{
              textTransform: "none",

              borderRadius: 2,

              color: textPrimary,

              borderColor,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={
              deleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Delete />
              )
            }
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          STATUS MODAL
      =================================================== */}

      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,

            overflow: "hidden",

            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,

            py: 2.5,

            color: textPrimary,

            fontWeight: 700,
          }}
        >
          {statusItem?.is_active
            ? "Deactivate Leave Type"
            : "Activate Leave Type"}
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            py: 3,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography
            sx={{
              color: textPrimary,

              fontWeight: 600,
            }}
          >
            {statusItem?.is_active
              ? "Are you sure you want to deactivate this leave type?"
              : "Are you sure you want to activate this leave type?"}
          </Typography>

          {statusItem && (
            <Box
              sx={{
                mt: 2,

                p: 1.8,

                border: `1px solid ${borderColor}`,

                borderRadius: 2,

                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,

                  color: textPrimary,
                }}
              >
                {statusItem.leave_name || "-"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.4,

                  color: textPrimary,
                }}
              >
                {statusItem.leave_code || "-"}
                {" • "}
                {statusItem.branch_name || "-"}
              </Typography>
            </Box>
          )}

          <Typography
            variant="body2"
            sx={{
              mt: 1.5,

              color: textPrimary,
            }}
          >
            {statusItem?.is_active
              ? "The leave type will remain in the table but will be marked as inactive."
              : "The leave type will become active and available again."}
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,

            py: 2,

            gap: 1,

            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseStatus}
            disabled={statusUpdating}
            sx={{
              textTransform: "none",

              borderRadius: 2,

              color: textPrimary,

              borderColor,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color={
              statusItem?.is_active ? "error" : "success"
            }
            onClick={handleStatusUpdate}
            disabled={statusUpdating}
            startIcon={
              statusUpdating ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",

              borderRadius: 2,

              fontWeight: 600,

              minWidth: 110,
            }}
          >
            {statusUpdating
              ? "Updating..."
              : statusItem?.is_active
              ? "Deactivate"
              : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveType;